import {
  DeleteOutlined,
  InfoCircleOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { Button, Checkbox, Form, Layout, Radio, Select } from 'antd';
import { useContext } from 'react';

import { SDetailLayout } from 'app/components/StaycationUI';
import { CurrencyInput } from 'app/components/commons/Currency/CurrencyInput/CurrencyInput';
import {
  CustomRow,
  DatePickerRow,
  InputNumberRow,
  InputRow,
  SelectRow,
  SwitchRow,
} from 'app/components/commons/CustomFields/CustomFields';
import { PictureComponent } from 'app/components/commons/PictureComponent/PictureComponent';
import { FormSection } from 'app/components/forms/FormSection';
import { UserContext } from 'app/context/UserContext/UserContext';
import { usePackages } from 'app/hooks/data/packages/usePackages';
import { useCreateVoucher } from 'app/hooks/data/useVoucher';
import { useAppSelector } from 'app/redux/hooks';
import { selectCountries } from 'app/redux/selectors/conf';
import type { VoucherForm } from 'app/typings/voucher';
import { copyToClipboard } from 'app/utils/clipboard';
import { dayjs } from 'app/utils/dayjs';
import { formatPackageName } from 'app/utils/packages';
import { generateCode } from 'app/utils/strings';
import { parseInputNumber } from 'app/utils/typing';

import './Vouchers.scss';

const { Content } = Layout;

const DISCOUNT_PERCENT_WARNING_THRESHOLD = 20;

export const Vouchers = () => {
  const initialValues = {
    giftCardType: 'voucher',
    valueType: 'FIX',
    code: generateCode(7),
    minAmount: 0,
    startDate: dayjs(),
    expirationDate: dayjs().add(1, 'year'),
    restrictedToCountry: false,
  };
  const [form] = Form.useForm<VoucherForm>();

  const { mutateAsync: createVoucher } = useCreateVoucher({
    onSuccess: (_, variables) => {
      form.resetFields();

      if (variables?.code) {
        copyToClipboard(variables.code);
      }
    },
  });
  const { user } = useContext(UserContext);

  const countries = useAppSelector(selectCountries);
  const sortedCountries = countries.sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  const onSubmit = (values: VoucherForm) => {
    if (!user) {
      return;
    }

    const formattedValues =
      values.giftCardType === 'incentive'
        ? {
            ...values,
            restrictedPackageIds: specificPackageOnly
              ? values.restrictedPackageIds
              : [],
            startDate: values.startDate,
            expirationDate: values.expirationDate,
            userId: user.id,
          }
        : {
            ...values,
            startDate: dayjs(),
            expirationDate: values.expirationDate,
            maxUse: 1,
            onlyFirstBooking: false,
            userId: user.id,
            valueType: 'FIX' as const,
          };

    createVoucher(formattedValues);
  };

  const reset = () => {
    form.resetFields();
    form.setFieldValue('code', generateCode(7));
  };

  const isGiftCardTypeIncentive =
    Form.useWatch('giftCardType', form) === 'incentive';
  const valueType = Form.useWatch('valueType', form) ?? initialValues.valueType;
  const discount = Form.useWatch('discount', form);
  const isValueTypeFixed = valueType === 'FIX';
  const specificPackageOnly = Form.useWatch('specificPackageOnly', form);

  return (
    <Layout className="voucher-layout">
      <SDetailLayout
        title="New Voucher"
        mode="edit"
        isDirty
        headerExtra={[
          <Button type="primary" size="large" onClick={reset} key="reset">
            Reset
          </Button>,
        ]}
      >
        <Content>
          <Form
            form={form}
            initialValues={initialValues}
            onFinish={onSubmit}
            layout="horizontal"
            labelCol={{ span: 6 }}
            wrapperCol={{ span: 18 }}
            onValuesChange={(changedValues) => {
              if (changedValues.giftCardType === 'incentive') {
                form.setFieldsValue({
                  startDate: dayjs(),
                  expirationDate: dayjs().add(3, 'months'),
                  onlyFirstBooking: false,
                });
              } else if (changedValues.giftCardType) {
                form.setFieldsValue({
                  startDate: dayjs(),
                  expirationDate: dayjs().add(1, 'year'),
                  valueType: 'FIX',
                });
              }
            }}
          >
            <div className="page-content">
              <FormSection title="Type" bordered>
                <div className="sectionSpacing">
                  <SelectRow
                    label="Type"
                    name="giftCardType"
                    dataSource={[
                      { id: 'voucher', name: 'Voucher' },
                      { id: 'incentive', name: 'Incentive' },
                      { id: 'internal', name: 'Internal' },
                      { id: 'manual', name: 'Manual Gift Card' },
                    ]}
                    required
                    noStyle
                  />
                </div>
              </FormSection>
              <FormSection title="Use conditions">
                <div className="sectionSpacing">
                  {isGiftCardTypeIncentive && (
                    <DatePickerRow
                      label="Start date"
                      name="startDate"
                      format="dddd DD MMMM YYYY"
                      required
                      noStyle
                    />
                  )}
                  <DatePickerRow
                    label="Expiration date"
                    name="expirationDate"
                    format="dddd DD MMMM YYYY"
                    required
                    noStyle
                  />
                  <SelectRow
                    label="Country"
                    name="countryId"
                    dataSource={sortedCountries}
                    placeholder="France"
                    required
                    noStyle
                  />
                  <div className="restrictedCountryRow">
                    <Form.Item
                      name="restrictedToCountry"
                      valuePropName="checked"
                      noStyle
                    >
                      <Checkbox />
                    </Form.Item>
                    <span>
                      Limit the use of the voucher only to the country to which
                      it is attached.
                    </span>
                  </div>
                  {isGiftCardTypeIncentive && (
                    <>
                      <InputNumberRow
                        label="Maximum code use"
                        name="maxUse"
                        placeholder="10"
                        min={1}
                        required
                        noStyle
                      />
                      <SwitchRow
                        label="1st booking only"
                        name="onlyFirstBooking"
                        noStyle
                      />
                    </>
                  )}
                </div>
              </FormSection>
              <FormSection title="Value">
                <div className="sectionSpacing">
                  {isGiftCardTypeIncentive && (
                    <CustomRow label="Value type" name="valueType" noStyle>
                      <Radio.Group>
                        <Radio value="FIX">Amount</Radio>
                        <Radio value="PERCENT">Percent</Radio>
                      </Radio.Group>
                    </CustomRow>
                  )}

                  {isValueTypeFixed ? (
                    <CustomRow label="Value" name="value" required noStyle>
                      <CurrencyInput
                        min={0}
                        currency="€"
                        parser={parseInputNumber}
                        placeholder="10"
                      />
                    </CustomRow>
                  ) : (
                    <CustomRow
                      label="Value"
                      name="discount"
                      required
                      noStyle
                      formItemNotice={
                        discount > DISCOUNT_PERCENT_WARNING_THRESHOLD && (
                          <span className="textError">
                            <InfoCircleOutlined className="infoIcon" />
                            Discount higher than 20% should be exceptional, make
                            sure that is what you want.
                          </span>
                        )
                      }
                    >
                      <CurrencyInput
                        min={0}
                        max={100}
                        currency="%"
                        parser={parseInputNumber}
                        placeholder="10"
                      />
                    </CustomRow>
                  )}

                  <CustomRow
                    label="Minimum use amount"
                    name="minAmount"
                    required
                    noStyle
                  >
                    <CurrencyInput
                      min={0}
                      currency="€"
                      parser={parseInputNumber}
                      placeholder="10"
                    />
                  </CustomRow>
                </div>
              </FormSection>

              {isGiftCardTypeIncentive && (
                <FormSection title="Package" bordered>
                  <div className="sectionSpacing">
                    <SwitchRow
                      label="Specific package only"
                      name="specificPackageOnly"
                      noStyle
                    />
                    {specificPackageOnly && <PackageListFormItem />}
                  </div>
                </FormSection>
              )}
              <FormSection title="Code">
                <div className="sectionSpacing">
                  <InputRow
                    label="Beneficiary"
                    name="beneficiary"
                    placeholder="Kevin Hutchings"
                    required
                    noStyle
                  />
                  <InputRow
                    label="Code"
                    name="code"
                    placeholder="ABCDEF"
                    required
                    noStyle
                  />
                </div>
              </FormSection>
            </div>
          </Form>
        </Content>
        <div className="sticky-footer">
          <Button type="primary" size="large" onClick={form.submit}>
            Save
          </Button>
        </div>
      </SDetailLayout>
    </Layout>
  );
};

const { Option } = Select;

const PackageListFormItem = () => {
  const { data: packages } = usePackages();

  return (
    <Form.List name="restrictedPackageIds">
      {(fields, { add, remove }) => (
        <>
          {fields.map((field, index) => (
            <CustomRow
              key={field.key}
              label={`Package #${index + 1}`}
              name={[field.name]}
              noStyle
            >
              <div className="selectPackage">
                <Form.Item name={[field.name]} noStyle>
                  <Select
                    className="package-list-select"
                    showSearch
                    optionFilterProp="children"
                    placeholder="Select a package from the list"
                    filterOption={(input, option) =>
                      option?.key.toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    {packages?.map((data) => (
                      <Option
                        key={`${data.hotelName}-${data.id}`}
                        value={data.id}
                        className="select-item__option"
                      >
                        <div className="select-item__wrapper">
                          <div className="select-item__thumbnail">
                            <PictureComponent {...data.coverPicture} />
                          </div>
                          <div>
                            {formatPackageName({
                              name: data.aliasForHotel,
                              hotelName: data.hotelName,
                            })}
                          </div>
                        </div>
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                <DeleteOutlined
                  className="deleteButton"
                  onClick={() => remove(field.name)}
                />
              </div>
            </CustomRow>
          ))}

          <CustomRow label="" name="">
            <Button onClick={add} icon={<PlusOutlined />}>
              Add package
            </Button>
          </CustomRow>
        </>
      )}
    </Form.List>
  );
};
